import React from 'react';
import { motion } from 'framer-motion';
import { Parallax } from 'react-scroll-parallax';
import styled from 'styled-components';
import FloatingElements from './FloatingElements';

const AboutSection = styled.section`
  padding: 150px 0 100px;
  background-image: url("/images/fondo-nosotros.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const TopContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ContentWrapper = styled.div`
  background-image: url("/images/fondo-texto.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 30px;
  border-radius: 20px;
  margin-top: -100px;
`;

const Title = styled(motion.h2)`
  font-family: "BRLNSDB";
  font-size: 2.5rem;
  color:rgb(255, 255, 255);
  margin-top: 1rem;
  margin-bottom: 1rem;
  position: relative;
  

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #5D9FFC 0%, #3d7edb 100%);

    @media (max-width: 968px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const Description = styled(motion.p)`
  color:rgb(255, 255, 255);
  line-height: 1.8;
  font-size: 1.1rem;
  font-family: "Montserrat";
  padding: 20px;

  
`;

const ImageWrapper = styled.div`
  position: relative;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 20px;
    bottom: 20px;
    border: 2px solid #591977;
    border-radius: 20px;
    z-index: 1;
    transform: translateZ(-10px);
    transition: all 0.3s ease;
  }

  &:hover:before {
    transform: translateZ(-20px) scale(1.05);
    border-color: #3d7edb;
  }
`;

const Image = styled(motion.img)`
  width: 100%;
  height: auto;
  border-radius: 20px;
  position: relative;
  z-index: -1;
  box-shadow: 0 20px 40px rgba(93, 159, 252, 0.15);
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
`;

const FeaturesGrid = styled.div`
  margin-top: 4rem;
`;

const FeatureGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  grid-column: 1 / -1;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GroupTitle = styled.h3`
  font-family: "BRLNSDB";
  font-size: 3rem;
  color: #2d3748;
  margin-bottom: 2rem;
  text-align: center;
  grid-column: 1 / -1;
  position: relative;
  padding-bottom: 1rem;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #5D9FFC 0%, #3d7edb 100%);
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(93, 159, 252, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #5D9FFC;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(93, 159, 252, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #5D9FFC 0%, #3d7edb 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(93, 159, 252, 0.15);

    &:before {
      transform: scaleX(1);
    }

    ${IconWrapper} {
      transform: scale(1.1);
      background: linear-gradient(135deg, #5D9FFC 0%, #3d7edb 100%);
      color: white;
    }
  }
`;

const FeatureTitle = styled.h3`
  font-family: "BRLNSDB";
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-family: "Montserrat";
  color: #4a5568;
  line-height: 1.6;
`;

const FloatingObject = styled(motion.div)`
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(93, 159, 252, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #5D9FFC;
  z-index: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(93, 159, 252, 0.2);
    transform: scale(1.1);
  }
`;

const About = () => {
  const floatingObjects = [
    { 
      icon: '📚', 
      top: '10%', 
      left: '1%',
      animation: {
        y: [0, -20, 0],
        rotate: [0, 10, -10, 0],
        scale: [1, 1.1, 1]
      }
    },
    { 
      icon: '✏️', 
      top: '20%', 
      right: '-3%',
      animation: {
        y: [0, 15, -15, 0],
        rotate: [0, -15, 15, 0],
        scale: [1, 1.05, 1]
      }
    },
    { 
      icon: '📐', 
      bottom: '15%', 
      left: '-3%',
      animation: {
        y: [0, -25, 25, 0],
        rotate: [0, 20, -20, 0],
        scale: [1, 1.15, 1]
      }
    },
    { 
      icon: '🎨', 
      bottom: '25%', 
      right: '-7%',
      animation: {
        y: [0, 20, -20, 0],
        rotate: [0, -25, 25, 0],
        scale: [1, 1.1, 1]
      }
    },
    { 
      icon: '🔢', 
      top: '90%', 
      right: '-2%',
      animation: {
        y: [0, -15, 15, 0],
        rotate: [0, 15, -15, 0],
        scale: [1, 1.05, 1]
      }
    },
    { 
      icon: '📖', 
      bottom: '40%', 
      left: '0%',
      animation: {
        y: [0, 25, -25, 0],
        rotate: [0, -20, 20, 0],
        scale: [1, 1.15, 1]
      }
    }
  ];

  const features = {
    why: {
      title: "¿Por qué elegir PedDigital?",
      items: [
        {
          icon: '📚',
          title: 'Contenidos DBA',
          description: 'Contenidos educativos basados en los DBA'
        },
        {
          icon: '🎯',
          title: 'Interfaz Intuitiva',
          description: 'Interfaz intuitiva y fácil de usar'
        },
        {
          icon: '💡',
          title: 'Recursos Interactivos',
          description: 'Recursos pedagógicos interactivos'
        },
        {
          icon: '📊',
          title: 'Evaluación Continua',
          description: 'Evaluaciones y seguimiento del aprendizaje'
        },
        {
          icon: '📱',
          title: 'Accesible',
          description: 'Accesible desde tabletas Android'
        },
        {
          icon: '👥',
          title: 'Apoyo Integral',
          description: 'Apoyo para docentes y estudiantes'
        },
        {
          icon: '🎨',
          title: 'Material Multimedia',
          description: 'Material didáctico multimedia'
        },
        {
          icon: '🚀',
          title: 'Proceso Educativo',
          description: 'Fortalece el proceso educativo'
        }
      ]
    }
  };

  return (
    <AboutSection id="about">
      <FloatingElements section="about" />
      <Container>
        <TopContent>
          <Parallax 
            translateY={[-30, 30]} 
            scale={[0.95, 1.05]}
            rotate={[-2, 2]}
            easing="easeInQuad"
            speed={10}
          >
           
              {floatingObjects.map((obj, index) => (
                <FloatingObject
                  key={index}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    ...obj.animation
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: index * 0.3,
                    ease: "easeInOut",
                    times: [0, 0.25, 0.5, 1]
                  }}
                  whileHover={{
                    scale: 1.2,
                    transition: { duration: 0.2 }
                  }}
                  style={{
                    top: obj.top,
                    left: obj.left,
                    right: obj.right,
                    bottom: obj.bottom
                  }}
                >
                  {obj.icon}
                </FloatingObject>
              ))}
              <Image 
                src="/images/sobre_ped.png" 
                alt="Sobre PedDigital"
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              />
          
          </Parallax>

          <ContentWrapper>
            <Title
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Sobre PedDigital
            </Title>
            <Description
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              PedDigital es una plataforma educativa innovadora diseñada para transformar la experiencia de aprendizaje. Combinamos tecnología avanzada con métodos pedagógicos probados para crear un entorno de aprendizaje efectivo y atractivo.
            </Description>
          </ContentWrapper>
        </TopContent>

        <FeaturesGrid>
          {Object.entries(features).map(([key, group], groupIndex) => (
            <FeatureGroup key={key}>
              <GroupTitle
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: groupIndex * 0.2 }}
              >
                {group.title}
              </GroupTitle>
              {group.items.map((feature, index) => (
                <Parallax
                  key={index}
                  translateY={['-10px', '10px']}
                  scale={[0.95, 1.05]}
                  easing="easeInQuad"
                >
                  <FeatureCard
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (groupIndex * 4 + index) * 0.1 }}
                  >
                    <IconWrapper>
                      {feature.icon}
                    </IconWrapper>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>{feature.description}</FeatureDescription>
                  </FeatureCard>
                </Parallax>
              ))}
            </FeatureGroup>
          ))}
        </FeaturesGrid>
      </Container>
    </AboutSection>
  );
};

export default About; 